<template>
  <v-btn
    class="sn-btn"
    :class="btnType"
    :disabled="disabled"
    :outline="outline"
    :block="block"
    @click="$emit('click')"
  >
    <!-- @slot Add content you wish the button to display in the default slot -->
    <slot />
  </v-btn>
</template>

<script>
export default {
  name: 'SnBtn',
  props: {
    /**
     * Defines the type of the button. Must be one of the following:
     * 'primary' | 'secondary' | 'caution' | 'warning'
     **/
    type: {
      type: String,
      default: 'primary',
      validator (type) {
        return ['primary', 'secondary', 'caution', 'warning'].includes(type)
      }
    },
    disabled: {
      type: Boolean,
      default: false
    },
    outline: {
      type: Boolean,
      default: false
    },
    /**
     * Makes the button taller and makes the font condensed.
     **/
    display: {
      type: Boolean,
      default: false
    },
    /**
     * Block buttons extend the full available width.
     **/
    block: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    btnType () {
      const type = [`sn-btn--${this.type}`]
      if (this.display) {
        type.push('sn-btn--display')
      }
      if (this.outline) {
        type.push('sn-btn--outline')
      }
      return type
    }
  }
}
</script>

<style lang="scss" scoped>
  @import '../../../assets/css/snui/index.scss';

  /* default btn styles */
  .theme--light.v-btn:not(.v-btn--flat):not(.v-btn--text):not(.v-btn--outlined) {
    &.sn-btn {
      border-radius: 0px;
      box-shadow: none;
      height: 32px;
      min-width: 122px;
      font-family: Barlow,Roboto,Arial,sans-serif;
      font-weight: 500;
      font-size: 15px;
      line-height: 20px;
      vertical-align: middle;
      letter-spacing: 1.5px;
      box-sizing: border-box;
      border: 1px solid $sn-black;
      color: $sn-white;

      &:disabled {
        cursor: not-allowed;
        opacity: 1;
        color: $sn-white !important;
      }

      // primary button styles
      &.sn-btn--primary {
        background-color: $sn-primary;

        &:disabled {
          background-color: $sn-primary-lighten-3 !important;
          border-color: $sn-primary-lighten-3;
          border-top-color: $sn-primary-lighten-3;
          border-right-color: $sn-primary-lighten-3;
          border-bottom-color: $sn-primary-lighten-3;
          border-left-color: $sn-primary-lighten-3;
        }
      }

      // secondary button styles
      &.sn-btn--secondary {
        background-color: $sn-white;
        color: $sn-primary;

        &:disabled {
          border-color: $sn-primary-lighten-3;
          color: $sn-primary-lighten-3 !important;
          background-color: $sn-white !important;
        }
      }

      // caution button styles
      &.sn-btn--caution {
        background-color: $sn-caution;
        border-color: $sn-caution;

        &:disabled {
          background-color: $sn-caution-lighten-3 !important;
          border-color: $sn-caution-lighten-3;
        }
      }

      // warning button styles
      &.sn-btn--warning {
        background-color: $sn-warning;
        border-color: $sn-warning;

        &:disabled {
          background-color: $sn-warning-lighten-3 !important;
          border-color: $sn-warning-lighten-3;
        }
      }

      // display button styles
      &.sn-btn--display {
        height: 52px;
        letter-spacing: 0.5px;
        font-family: 'Barlow Condensed', sans-serif;
        font-size: 20px;
        line-height: 25px;
        font-weight: 500 !important;
      }

      // outline button styles
      &.sn-btn--outline {
        color: #000000;
      }
    }
  }
</style>
